import { ProximityContext } from "@inrange/building-manager-api-client";

// Shared rendering of a lead's proximity anchor - the operational InRange site the
// operator was searching near when the lead was promoted (snapshotted at promote
// time). Used on the leads inbox rows and the lead edit pane so they read the same.

const formatDistance = (metres: number | null): string => {
  if (metres === null) return "";
  if (metres < 1000) return `${Math.round(metres)} m`;
  return `${(metres / 1000).toFixed(1)} km`;
};

// The chip text ("near <site> · <dist>").
const proximityLabel = (proximity: ProximityContext): string => {
  const name = proximity.anchorSiteName ?? "an operational site";
  const dist = formatDistance(proximity.distanceMetres);
  return `near ${name}${dist ? ` · ${dist}` : ""}`;
};

const proximityTooltip = (proximity: ProximityContext): string => {
  const name = proximity.anchorSiteName ?? "an operational site";
  const dist = formatDistance(proximity.distanceMetres);
  const when = proximity.capturedAt
    ? `, captured ${new Date(proximity.capturedAt).toLocaleDateString()}`
    : "";
  return `Found by searching near ${name}${dist ? ` (${dist} away)` : ""}${when}`;
};

// A small "near <site>" chip, styled like the sibling confidence chips.
export const ProximityChip: React.FC<{
  proximity: ProximityContext | null;
}> = ({ proximity }) => {
  if (!proximity) return null;
  return (
    <span
      title={proximityTooltip(proximity)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 999,
        padding: "2px 9px",
        whiteSpace: "nowrap",
        background: "#e7f1ff",
        color: "#0a58ca",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "currentColor",
          opacity: 0.85,
        }}
      />
      {proximityLabel(proximity)}
    </span>
  );
};
